import { Temporality } from "./temporality";
import { Dataprovider } from "../../data-provider/model/dataprovider";
import { AisProduct } from "src/app/shared/enums/AisProducts";
import { AiracType } from "src/app/shared/enums/AiracTypes";
import { NotamType } from "src/app/shared/enums/NotamTypes";
import moment, { isMoment } from "moment";

/**
 * DTO for displaying a list of Update Notice.
 *
 */

export class UpdateNoticeSummaryDTO {
  // Mandatory fields
  valueOfFeatureId: string;
  status: string;
  displayedStatus: string;
  name: string;
  effectiveDate: Date | moment.Moment;
  cancelDate: Date | moment.Moment | null;
  temporality: Temporality | Temporality[];
  originator: Dataprovider;
  timezone?: string | null;
  aisProduct: AisProduct;
  airacType?: AiracType | AiracType[] | null;
  estimated: boolean;
  notamType?: NotamType | NotamType[] | null;
  referredSeries?: string | null;
  referredNumber?: number | null;
  referredYear?: number | null;
  notamText?: string | null;
  notamReasonForRejection?: string | null;
  firAerodromeAirfield?: string | null;
  lowerLimit?: string | null;
  upperLimit?: string | null;
  sectionsToBeAmended?: string | null;
  paragraphToBeAmended?: string | null;
  textToBeAmended?: string | null;

  constructor(init?: Partial<UpdateNoticeSummaryDTO>) {
    Object.assign(this, init);
  }

  /**
   * Parse a partial DTO (from form data, API responses, etc.)
   */
  static parse(data: Partial<UpdateNoticeSummaryDTO>): UpdateNoticeSummaryDTO {
    const parsed = new UpdateNoticeSummaryDTO();
    parsed.valueOfFeatureId = data.valueOfFeatureId!;
    parsed.name = data.name!;
    parsed.temporality = Array.isArray(data.temporality) ? data.temporality[0] : [data.temporality];
    parsed.effectiveDate = isMoment(data.effectiveDate)
      ? data.effectiveDate
      : moment(data.effectiveDate).tz(data.timezone ?? "UTC");
    parsed.cancelDate = data.cancelDate
      ? isMoment(data.cancelDate)
        ? data.cancelDate
        : moment(data.cancelDate).tz(data.timezone ?? "UTC")
      : null;

    parsed.status = data.status;
    parsed.displayedStatus = this.parseDisplayedStatus(data.status);
    parsed.originator = data.originator ?? null;
    parsed.timezone = data.timezone ?? "UTC";
    parsed.aisProduct = data.aisProduct!;
    parsed.airacType = Array.isArray(data.airacType) ? data.airacType[0] : [data.airacType];
    parsed.estimated = data.estimated ?? false;
    parsed.notamType =
      parsed.aisProduct === AisProduct.NOTAM
        ? Array.isArray(data.notamType)
          ? data.notamType[0]
          : [data.notamType]
        : null;
    parsed.referredSeries = data.referredSeries ?? null;
    parsed.referredNumber = data.referredNumber ?? null;
    parsed.referredYear = data.referredYear ?? null;
    parsed.notamText = data.notamText ?? null;
    parsed.notamReasonForRejection = data.notamReasonForRejection ?? null;
    parsed.firAerodromeAirfield = data.firAerodromeAirfield ?? null;
    parsed.lowerLimit = data.lowerLimit ?? null;
    parsed.upperLimit = data.upperLimit ?? null;
    parsed.sectionsToBeAmended = data.sectionsToBeAmended ?? null;
    parsed.paragraphToBeAmended = data.paragraphToBeAmended ?? null;
    parsed.textToBeAmended = data.textToBeAmended ?? null;

    return parsed;
  }

  static parseDisplayedStatus(status: string): string {
    const statusDict = {
      ["task_edit_change_request"]: "Editable",
      ["task_saf_review"]: "SAF Review",
      ["task_nof_review"]: "NOF Review",
      ["task_nof_perm_notam"]: "NOF Perm NOTAM",
      ["task_ais_review"]: "AIS Review",
      ["end_implemented_with_perm_notam"]: "Implemented with perm NOTAM",
      ["end_implemented"]: "Implemented",
      ["end_notam_request_accepted"]: "NOTAM Accepted",
      ["end_cancelled"]: "Cancelled",
    };
    // Return the value from the dictionary or fallback to original status
    return statusDict[status] ?? status;
  }
}
